/**
 * Плавающая панель действий над выделенными пинами — внизу рабочей области,
 * по центру относительно `main` (сайдбар слева не в счёт), ниже просмотра пина.
 *
 * Действия: теги — одно меню «Теги» (повесить существующий или новый, у висящего
 * «×» — снять), положить в библиотеку. Тег — мгновенная метка, библиотека — оригинал с Pinterest на
 * каждый пин: поэтому «в библиотеку» идёт порциями с ходом «кладу d из t…» и
 * итогом, а отказ одного пина — строка в списке ошибок, не провал пачки.
 *
 * Сама панель ничего не знает о кэше: мутации даёт владелец (`References`).
 */
import { ChevronDown, ImagePlus, Shirt, Tag as TagIcon, X } from "lucide-react";
import { useCallback, useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router";

import { LibraryPicker } from "@/components/library/LibraryPicker";
import { TagPicker, usePopoverClose } from "@/components/trends/TagPicker";
import { btn, btnGhost, ErrorLine } from "@/components/trends/ui";
import { num } from "@/lib/media";
import type { BulkResult, Tag } from "@/lib/tags";

type Open = "tags" | "lib" | null;

export function SelectionBar({
  count,
  visibleCount,
  onSelectVisible,
  onClear,
  tags,
  tagCounts,
  onTag,
  onCreateTag,
  onUntag,
  defaultLibraryName,
  lastLibraryId,
  onToLibrary,
  busy,
  error,
  fitting,
}: {
  count: number;
  visibleCount: number;
  onSelectVisible: () => void;
  onClear: () => void;
  tags: Tag[];
  tagCounts: ReadonlyMap<number, number>;
  onTag: (tagId: number) => Promise<void>;
  onCreateTag: (name: string) => Promise<void>;
  onUntag: (tagId: number) => Promise<void>;
  defaultLibraryName: string;
  lastLibraryId: number | null;
  onToLibrary: (
    libraryId: number,
    name: string,
    onProgress: (done: number, total: number) => void,
  ) => Promise<BulkResult>;
  busy: boolean;
  error: unknown;
  /** Вход в примерку: адрес, число выделенных и потолок пар за раз. */
  fitting?: { to: string; count: number; max: number };
}) {
  const [open, setOpen] = useState<Open>(null);
  const close = useCallback(() => setOpen(null), []);
  const toggle = (what: Exclude<Open, null>) => setOpen((v) => (v === what ? null : what));
  // Клик по кнопке открытого поповера не должен сначала закрыть его «кликом мимо».
  const keep = (e: MouseEvent) => e.stopPropagation();

  const run = (job: Promise<void>) => job.then(close, () => undefined);

  return (
    <div className="pointer-events-none fixed right-0 bottom-4 left-[var(--spacing-sidebar)] z-40 flex justify-center px-4">
      <div
        className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-[var(--radius-card)] border border-border bg-bg-elevated px-3 py-2 text-[12.5px] shadow-xl"
        data-annot="панель выделения"
      >
        <span className="font-medium text-fg tnum">Выделено {num(count)}</span>
        <button
          type="button"
          className={btnGhost}
          onClick={onSelectVisible}
          disabled={visibleCount === 0}
          title="Добавить к выделению все пины под текущим фильтром"
        >
          все видимые ({num(visibleCount)})
        </button>
        <button type="button" className={btnGhost} onClick={onClear} title="Снять выделение (Esc)">
          <X size={12} />
          снять
        </button>
        {fitting ? (
          fitting.count > fitting.max ? (
            <span
              className={`${btn} cursor-not-allowed opacity-50`}
              aria-disabled="true"
              title={`не больше ${fitting.max} за раз`}
            >
              <Shirt size={13} />
              Генерировать {num(fitting.count)}
            </span>
          ) : (
            <Link className={btn} to={fitting.to} title="Примерить платья на выделенные референсы">
              <Shirt size={13} />
              Генерировать {num(fitting.count)}
            </Link>
          )
        ) : null}

        <span className="h-5 w-px bg-border" />

        <div className="relative">
          <button
            type="button"
            className={btn}
            aria-expanded={open === "tags"}
            onMouseDown={keep}
            onClick={() => toggle("tags")}
            disabled={busy}
          >
            <TagIcon size={13} />
            Теги
            <ChevronDown size={12} />
          </button>
          {open === "tags" ? (
            <TagPicker
              tags={tags}
              counts={tagCounts}
              total={count}
              mode="both"
              busy={busy}
              onPick={(id) => void run(onTag(id))}
              onUnpick={(id) => void run(onUntag(id))}
              onCreate={(name) => void run(onCreateTag(name))}
              onClose={close}
            />
          ) : null}
        </div>
        <div className="relative">
          <button
            type="button"
            className={btn}
            aria-expanded={open === "lib"}
            onMouseDown={keep}
            onClick={() => toggle("lib")}
          >
            <ImagePlus size={13} />В библиотеку
          </button>
          {open === "lib" ? (
            <LibraryCard
              count={count}
              defaultLibraryName={defaultLibraryName}
              lastLibraryId={lastLibraryId}
              onToLibrary={onToLibrary}
              onClose={close}
            />
          ) : null}
        </div>
        {error ? (
          <div className="basis-full">
            <ErrorLine error={error} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LibraryCard({
  count,
  defaultLibraryName,
  lastLibraryId,
  onToLibrary,
  onClose,
}: {
  count: number;
  defaultLibraryName: string;
  lastLibraryId: number | null;
  onToLibrary: (
    libraryId: number,
    name: string,
    onProgress: (done: number, total: number) => void,
  ) => Promise<BulkResult>;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<(BulkResult & { name: string }) | null>(null);
  const [error, setError] = useState<unknown>(null);
  const working = progress !== null && result === null && error === null;
  // Пока кладём, клик мимо карточку не закрывает: ход и итог должны остаться видны.
  usePopoverClose(box, working ? noop : onClose);

  const put = async (libraryId: number, name: string) => {
    setResult(null);
    setError(null);
    setProgress({ done: 0, total: count });
    try {
      const got = await onToLibrary(libraryId, name, (done, total) => setProgress({ done, total }));
      setResult({ ...got, name });
    } catch (exc) {
      setError(exc);
    }
  };

  return (
    <div
      ref={box}
      className="absolute right-0 bottom-full z-10 mb-2 grid w-[320px] gap-2 rounded-[var(--radius-card)] border border-border bg-bg p-3 shadow-xl"
      data-annot="выделенные в библиотеку"
    >
      <div className="text-[12.5px] font-medium text-fg">Положить {num(count)} в библиотеку референсов</div>
      <div className="text-[11.5px] text-fg-faint">
        У пинов без оригинала он скачается с Pinterest (~1 с на пин); у видео ляжет обложка.
      </div>
      <LibraryPicker
        initialLibraryId={lastLibraryId}
        defaultName={defaultLibraryName}
        busy={working}
        submitLabel={`Положить ${num(count)}`}
        onPick={put}
      />
      {working ? (
        <div className="text-fg-muted tnum">
          кладу {num(progress.done)} из {num(progress.total)}…
        </div>
      ) : null}
      {result ? (
        <div className="grid gap-1">
          <div className="rounded-[var(--radius-ui)] bg-ok-bg px-2 py-1.5 text-ok">
            «{result.name}»: положено {num(result.added)}, уже было {num(result.already)}, ошибок{" "}
            {num(result.errors.length)}
          </div>
          {result.errors.length ? (
            <ul className="max-h-[120px] overflow-auto text-[11.5px] text-err">
              {result.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <ErrorLine error={error} />
    </div>
  );
}

const noop = () => undefined;
